import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalLayout from '../layouts/ModalLayout';
import Input from '../misc/Input';

const EMOJI_LIST: Array<{ emoji: string; name: string }> = [
  { emoji: '👍', name: 'thumbs up like' },
  { emoji: '❤️', name: 'heart love' },
  { emoji: '😂', name: 'laugh cry funny' },
  { emoji: '😮', name: 'wow surprised' },
  { emoji: '😢', name: 'sad cry tear' },
  { emoji: '😡', name: 'angry mad' },
  { emoji: '🎉', name: 'party celebrate' },
  { emoji: '🔥', name: 'fire hot' },
  { emoji: '👏', name: 'clap applause' },
  { emoji: '🙏', name: 'pray thank you' },
  { emoji: '😍', name: 'love eyes heart' },
  { emoji: '😊', name: 'smile happy' },
  { emoji: '💪', name: 'strong muscle' },
  { emoji: '😎', name: 'cool sunglasses' },
  { emoji: '🤔', name: 'thinking hmm' },
  { emoji: '🥳', name: 'party celebrate birthday' },
  { emoji: '😭', name: 'cry sobbing' },
  { emoji: '💯', name: 'hundred perfect' },
  { emoji: '✅', name: 'check done complete' },
  { emoji: '⭐', name: 'star favourite' },
  { emoji: '🚀', name: 'rocket launch' },
  { emoji: '🎯', name: 'target goal bullseye' },
  { emoji: '💡', name: 'idea lightbulb' },
  { emoji: '📌', name: 'pin location' },
];

interface PostEmojiPickerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelectEmoji: (emoji: string) => void;
}

function EmojiGrid({
  items,
  onSelect,
}: {
  items: Array<{ emoji: string; name: string }>;
  onSelect: (emoji: string) => void;
}): JSX.Element {
  return (
    <div className="grid max-h-48 grid-cols-6 gap-1 overflow-y-auto">
      {items.map((item) => (
        <button
          key={item.emoji}
          type="button"
          onClick={() => onSelect(item.emoji)}
          className="flex h-10 items-center justify-center rounded-lg text-2xl hover:bg-secondary-200">
          {item.emoji}
        </button>
      ))}
    </div>
  );
}

export default function PostEmojiPicker({
  open,
  setOpen,
  onSelectEmoji,
}: PostEmojiPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return EMOJI_LIST;
    return EMOJI_LIST.filter((item) => item.name.includes(q) || item.emoji.includes(q));
  }, [search]);

  const handleSelect = (emoji: string): void => {
    onSelectEmoji(emoji);
    setSearch('');
    setOpen(false);
  };

  const handleClose = (value: boolean): void => {
    if (!value) setSearch('');
    setOpen(value);
  };

  const hasResults = filtered.length > 0;

  return (
    <ModalLayout open={open} setOpen={handleClose} className="w-72 rounded-2xl bg-secondary-50 p-4">
      <div className="flex flex-col">
        <Input
          id="post-emoji-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('page.communicationOverview.posts.emojiSearch')}
          className="mb-3 w-full rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm"
        />
        {!hasResults && (
          <p className="py-4 text-center text-sm text-primary-600">
            {t('page.communicationOverview.posts.emojiNoResults')}
          </p>
        )}
        {hasResults && <EmojiGrid items={filtered} onSelect={handleSelect} />}
      </div>
    </ModalLayout>
  );
}
